import { useNavigate, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  LayoutDashboard, Shirt, CalendarDays, LogOut, Settings, Instagram, Upload,
  Image, Truck, Type, FolderTree, Sparkles, Grid3X3, Tag, Clock, HelpCircle, Star, Ruler, BarChart3, Gem, CalendarRange, UserCheck, Mail, Users, ScrollText, TrendingUp, ChevronDown, Receipt, Menu, X, Gift
} from "lucide-react";
import { Button } from "../ui/button";

// ──────────────────────────────────────────────────────────────────────────────
// Sidebar grouping — 5 sections, fully collapsible, localStorage-backed.
// Each `to` MUST match a real route in App.js. No pages were deleted or merged.
// `exact: true` on Dashboard so /admin/* sub-paths don't all light up.
// ──────────────────────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    // P-4 — was "Main" containing Dashboard + Analytics + Costs + Audit Log.
    // Split into a tight "Overview" (2 items) + a dedicated
    // "Operations & Compliance" group below.
    id: "main",
    label: "Overview",
    items: [
      { to: "/admin",                   label: "Dashboard",        icon: LayoutDashboard, exact: true },
      { to: "/admin/analytics",         label: "Analytics",        icon: BarChart3 },
      { to: "/admin/analytics/trials",  label: "Trial Analytics",  icon: TrendingUp },
    ],
  },
  {
    id: "operations",
    label: "Operations & Compliance",
    items: [
      { to: "/admin/costs",             label: "Costs",            icon: Receipt },
      { to: "/admin/audit-log",         label: "Audit Log",        icon: ScrollText },
    ],
  },
  {
    id: "bookings",
    label: "Bookings & Customers",
    items: [
      { to: "/admin/bookings",      label: "Bookings",     icon: CalendarDays },
      { to: "/admin/customers",     label: "Customers",    icon: Users },
      { to: "/admin/rental-credits", label: "Rental Credit", icon: Gift },
      { to: "/admin/appointments",  label: "Appointments", icon: Clock },
      { to: "/admin/calendar",      label: "Schedule",     icon: CalendarRange },
      { to: "/admin/trial-leads",   label: "Trial Leads",  icon: UserCheck },
      { to: "/admin/trial-slots",   label: "Trial Slots",  icon: CalendarRange },
      { to: "/admin/communications", label: "Communications", icon: Mail },
      { to: "/admin/review-requests", label: "Review Requests", icon: Mail },
      { to: "/admin/notifications", label: "Notifications", icon: Mail },
    ],
  },
  {
    id: "catalogue",
    label: "Catalogue",
    items: [
      { to: "/admin/outfits",           label: "Collection",      icon: Shirt },
      { to: "/admin/catalogue",         label: "Catalogue Edit",  icon: Grid3X3 },
      { to: "/admin/categories",        label: "Categories",      icon: FolderTree },
      { to: "/admin/event-types",       label: "Event Types",     icon: Tag },
      { to: "/admin/size-guide",        label: "Size Guide",      icon: Ruler },
      { to: "/admin/upload",            label: "Batch Upload",    icon: Upload },
      { to: "/admin/looks",             label: "Looks Manager",   icon: Gem },
      { to: "/admin/feature-toggles",   label: "Feature Toggles", icon: Sparkles },
    ],
  },
  {
    id: "content",
    label: "Website Content",
    items: [
      { to: "/admin/hero-settings", label: "Hero Section", icon: Image },
      { to: "/admin/social",        label: "Social Feed",  icon: Instagram },
      { to: "/admin/reviews",       label: "Reviews",      icon: Star },
      { to: "/admin/business-reviews", label: "Google Reviews", icon: Star },
      { to: "/admin/faqs",          label: "FAQs",         icon: HelpCircle },
      { to: "/admin/site-texts",    label: "Site Text",    icon: Type },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    items: [
      { to: "/admin/delivery-settings",    label: "Delivery",         icon: Truck },
      { to: "/admin/booking-types",        label: "Booking Types",    icon: Settings },
      { to: "/admin/appointment-settings", label: "Appt. Settings",   icon: Settings },
      { to: "/admin/trial-reminders",      label: "Trial Reminders",  icon: Clock },
      { to: "/admin/settings",             label: "General Settings", icon: Settings },
    ],
  },
];

const LS_KEY = "mahra:admin:sidebarGroups";

function isItemActive(pathname, item) {
  if (item.exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(item.to + "/");
}

function loadInitialOpenState(pathname) {
  // Default: all groups open. Then merge whatever the user previously set.
  // Finally, force the group containing the active route to be open.
  const def = Object.fromEntries(NAV_GROUPS.map((g) => [g.id, true]));
  let saved = {};
  try {
    const raw = typeof window !== "undefined" && window.localStorage.getItem(LS_KEY);
    if (raw) saved = JSON.parse(raw) || {};
  } catch { /* ignore parse errors */ }
  const merged = { ...def, ...saved };
  const activeGroup = NAV_GROUPS.find((g) => g.items.some((it) => isItemActive(pathname, it)));
  if (activeGroup) merged[activeGroup.id] = true;
  return merged;
}

export default function AdminSidebar({ isMobileOpen = false, onMobileClose = () => {} } = {}) {
  const navigate = useNavigate();
  const location = useLocation();

  const [openGroups, setOpenGroups] = useState(() => loadInitialOpenState(location.pathname));

  // Auto-open the group that contains the currently-active route after every nav.
  useEffect(() => {
    const activeGroup = NAV_GROUPS.find((g) => g.items.some((it) => isItemActive(location.pathname, it)));
    if (!activeGroup) return;
    setOpenGroups((prev) => (prev[activeGroup.id] ? prev : { ...prev, [activeGroup.id]: true }));
  }, [location.pathname]);

  // Close the mobile drawer whenever the route ACTUALLY changes.
  // NOTE: `onMobileClose` must NOT be in the deps — the parent passes a
  // new arrow reference every render, which would fire this effect on
  // every state update and slam the drawer shut the instant it opened.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      onMobileClose();
    }
  }, [location.pathname, onMobileClose]);

  // Persist on change.
  useEffect(() => {
    try { window.localStorage.setItem(LS_KEY, JSON.stringify(openGroups)); } catch { /* quota */ }
  }, [openGroups]);

  const toggleGroup = useCallback((id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  // Memoise active-flag lookup so renders stay cheap.
  const itemActiveMap = useMemo(() => {
    const m = new Map();
    NAV_GROUPS.forEach((g) => g.items.forEach((it) => m.set(it.to, isItemActive(location.pathname, it))));
    return m;
  }, [location.pathname]);

  const navContent = (
    <>
      {/* Logo — opens live site in new tab. Identical styling to legacy version. */}
      <div className="flex-shrink-0 px-6 pt-6 pb-8 flex items-center justify-between">
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group" data-testid="admin-logo-link">
          <svg className="w-6 h-6 text-[#F0D9D5] group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C12 2 9 5 9 8C9 9.5 9.5 10.5 10 11.5C8.5 11 7 10.5 5 11C5 11 8 14 10.5 14C10 15 9 16.5 9 18C9 18 12 15 12 12C12 15 15 18 15 18C15 16.5 14 15 13.5 14C16 14 19 11 19 11C17 10.5 15.5 11 14 11.5C14.5 10.5 15 9.5 15 8C15 5 12 2 12 2Z" />
          </svg>
          <span className="text-lg text-white tracking-[0.15em] group-hover:text-[#F0D9D5] transition-colors">MAHRA</span>
        </a>
        {/* Close button — only visible in the mobile drawer */}
        <button
          type="button"
          onClick={onMobileClose}
          className="lg:hidden text-white/60 hover:text-white p-1"
          aria-label="Close menu"
          data-testid="admin-mobile-close"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Scrollable nav area */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-2 scrollbar-thin" data-testid="admin-nav">
        {NAV_GROUPS.map((group) => {
          const open = !!openGroups[group.id];
          const groupHasActive = group.items.some((it) => itemActiveMap.get(it.to));
          return (
            <div key={group.id} data-testid={`admin-nav-group-${group.id}`}>
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm uppercase whitespace-nowrap transition-colors ${
                  groupHasActive ? "text-[#D4B8B4]" : "text-white/55 hover:text-white/80"
                }`}
                data-testid={`admin-nav-group-toggle-${group.id}`}
                aria-expanded={open}
              >
                <span>{group.label}</span>
                <ChevronDown
                  className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`}
                  strokeWidth={2}
                />
              </button>
              {open && (
                <div className="space-y-0.5 mt-0.5" data-testid={`admin-nav-group-items-${group.id}`}>
                  {group.items.map((item) => {
                    const active = itemActiveMap.get(item.to);
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`flex items-center gap-3 px-3 py-2 text-sm rounded transition-colors ${
                          active
                            ? "bg-[#9A7B4F]/20 text-[#D4B8B4]"
                            : "text-white/50 hover:text-white hover:bg-white/5"
                        }`}
                        data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                        <span className="truncate">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Logout button — always pinned at bottom */}
      <div className="flex-shrink-0 p-4 border-t border-white/5">
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full justify-start border-white/10 text-white/50 hover:text-white hover:border-white/20 bg-transparent text-sm"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Logout
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar — hidden on <lg */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-[#6B5B4F] hidden lg:flex flex-col z-30" data-testid="admin-sidebar">
        {navContent}
      </aside>

      {/* Mobile drawer — hidden on lg+. Uses z-[100] so nothing on the
          page (sticky top bar at z-40, modal overlays at z-50) can sit
          on top of it. */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[100]"
          data-testid="admin-sidebar-mobile-drawer"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={onMobileClose}
            aria-hidden
            data-testid="admin-sidebar-mobile-backdrop"
          />
          <aside
            className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[#6B5B4F] flex flex-col shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {navContent}
          </aside>
        </div>
      )}
    </>
  );
}

/**
 * Wrapper layout for admin pages:
 *   <AdminLayout title="Page Title" subtitle="Optional">
 *     {children}
 *   </AdminLayout>
 */
export function AdminLayout({ children, title, subtitle, actions }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  return (
    <div className="h-screen flex overflow-hidden bg-[#FAF8F0]">
      <AdminSidebar isMobileOpen={mobileOpen} onMobileClose={closeMobile} />
      <main className="flex-1 lg:ml-64 overflow-y-auto" data-testid="admin-main-content">
        {/* Mobile top bar — only visible < lg. Provides the hamburger so admins can reach every page (Trial Slots included). */}
        <div className="lg:hidden sticky top-0 z-40 bg-[#6B5B4F] text-white flex items-center justify-between px-4 py-3 shadow-sm">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="p-1.5 -ml-1.5 text-white"
            aria-label="Open menu"
            data-testid="admin-mobile-menu-btn"
          >
            <Menu className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <span className="text-sm tracking-[0.15em] uppercase">{title || "MAHRA Admin"}</span>
          <span className="w-5" />
        </div>
        <div className="p-6 md:p-10">
          {(title || actions) && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
              <div>
                {title && <h1 className="text-3xl text-[#676664]">{title}</h1>}
                {subtitle && <p className="text-[#676664]/40 text-sm font-light">{subtitle}</p>}
              </div>
              {actions && <div className="flex items-center gap-3">{actions}</div>}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
